import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader2, ShieldCheck, Download, RotateCcw, Search, Bookmark, Trash2, Archive, ChevronDown, Users } from "lucide-react";

const EMPTY = {
  user_q: "", lead_id: "", transaction_id: "", statement: "",
  action: "all", field: "all", date_from: "", date_to: "", scope: "all",
};

const actionStyle = (a) =>
  ({ excluded: "border-red-200 bg-red-50 text-red-700",
     restored: "border-green-200 bg-ok text-ok-foreground",
     marked_duplicate: "border-warn-border bg-warn text-warn-foreground",
     field_changed: "border-blue-200 bg-secondary text-secondary-foreground",
     document_uploaded: "border-indigo-200 bg-indigo-50 text-indigo-700",
     document_deleted: "border-red-200 bg-red-50 text-red-700",
     row_added: "border-green-200 bg-ok text-ok-foreground",
     row_deleted: "border-red-200 bg-red-50 text-red-700" }[a] ||
   "bg-muted text-muted-foreground");

const when = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso
    : `${fmtDate(iso)} · ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
};

export default function AuditLogs() {
  const { user } = useAuth();
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState("");
  const [presetName, setPresetName] = useState("");
  const [retention, setRetention] = useState(null);
  const [activity, setActivity] = useState(null);
  const [showActivity, setShowActivity] = useState(false);

  const buildParams = (extra = {}, f = filters) => {
    const p = new URLSearchParams();
    Object.entries({ ...f, ...extra }).forEach(([k, v]) => {
      if (v && v !== "all") p.append(k, v);
    });
    return p;
  };

  const load = async (p = 1, f = filters) => {
    setBusy(true);
    try {
      const q = buildParams({ page: p, page_size: 25 }, f);
      const { data: res } = await api.get(`/audit/search?${q.toString()}`);
      setData(res);
      setPage(res.page);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1); loadPresets(); loadRetention(); }, []);

  const loadPresets = async () => {
    try { const { data: p } = await api.get("/audit/presets"); setPresets(p); } catch { /* admin only */ }
  };
  const loadRetention = async () => {
    try { const { data: r } = await api.get("/audit/retention"); setRetention(r); } catch { /* admin only */ }
  };

  const loadActivity = async (f = filters) => {
    try {
      const q = new URLSearchParams();
      if (f.date_from) q.append("date_from", f.date_from);
      if (f.date_to) q.append("date_to", f.date_to);
      if (f.scope) q.append("scope", f.scope);
      const { data: a } = await api.get(`/audit/activity-summary?${q.toString()}`);
      setActivity(a);
      setShowActivity(true);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const applyPreset = (id) => {
    setPresetId(id);
    const p = presets.find((x) => x.preset_id === id);
    if (!p) return;
    const merged = { ...EMPTY, ...p.filters };
    setFilters(merged);
    setPresetName(p.name);
    load(1, merged);
    toast.success(`Preset "${p.name}" applied`);
  };

  const savePreset = async () => {
    if (!presetName.trim()) return toast.error("Give the preset a name");
    try {
      if (presetId) {
        await api.put(`/audit/presets/${presetId}`, { name: presetName, filters });
        toast.success("Preset updated");
      } else {
        const { data: p } = await api.post("/audit/presets", { name: presetName, filters });
        setPresetId(p.preset_id);
        toast.success("Preset saved");
      }
      loadPresets();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deletePreset = async () => {
    if (!presetId) return;
    try {
      await api.delete(`/audit/presets/${presetId}`);
      setPresetId(""); setPresetName("");
      toast.success("Preset deleted");
      loadPresets();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const changeRetention = async (years) => {
    try {
      const { data: r } = await api.put("/audit/retention", { years: Number(years) });
      setRetention(r);
      toast.success(`Retention set to ${years} year(s) — older records are archived, never deleted`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const runArchive = async () => {
    try {
      const { data: r } = await api.post("/audit/archive", {});
      setRetention(r);
      toast.success(`${r.archived} record(s) moved to the immutable archive`);
      load(1);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportAs = (fmt) => {
    const q = buildParams({ fmt });
    window.open(`${API}/audit/export?${q.toString()}`, "_blank");
  };

  if (user && user.role !== "admin")
    return (
      <div className="rounded-lg border border-border bg-white p-10 text-center" data-testid="audit-forbidden">
        <ShieldCheck className="mx-auto h-6 w-6 text-primary" />
        <h1 className="mt-4 font-display text-xl font-semibold">Administrator access required</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Audit records are immutable and visible only to administrators and the owner of a statement.
        </p>
      </div>
    );

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Administration</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tighter">Audit Log</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Immutable, append-only record of every manual correction, exclusion and duplicate decision.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full bg-white" onClick={() => exportAs("csv")}
                  data-testid="audit-export-csv-btn"><Download className="mr-2 h-4 w-4" /> CSV</Button>
          <Button variant="outline" className="rounded-full bg-white" onClick={() => exportAs("json")}
                  data-testid="audit-export-json-btn"><Download className="mr-2 h-4 w-4" /> JSON</Button>
          <Button className="rounded-full" onClick={() => exportAs("xlsx")}
                  data-testid="audit-export-xlsx-btn"><Download className="mr-2 h-4 w-4" /> XLSX</Button>
        </div>
      </div>

      <div className="grid gap-3 rounded-lg border border-border bg-white p-4 lg:grid-cols-[1.4fr_1fr]"
           data-testid="audit-admin-tools">
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[190px] flex-1">
            <p className="mb-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">Saved presets</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-between bg-white"
                        data-testid="audit-preset-select">
                  {presets.find((p) => p.preset_id === presetId)?.name
                    || (presets.length ? "Apply a preset" : "No presets yet")}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 bg-white/90 backdrop-blur-xl">
                {presets.length === 0 && (
                  <DropdownMenuItem disabled>No saved presets</DropdownMenuItem>
                )}
                {presets.map((p) => (
                  <DropdownMenuItem key={p.preset_id} onClick={() => applyPreset(p.preset_id)}
                                    data-testid={`audit-preset-item-${p.preset_id}`}>
                    {p.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Input value={presetName} onChange={(e) => setPresetName(e.target.value)} maxLength={60}
                 placeholder="Preset name" className="w-44 bg-white" data-testid="audit-preset-name-input" />
          <Button variant="outline" className="bg-white" onClick={savePreset} data-testid="audit-preset-save-btn">
            <Bookmark className="mr-2 h-4 w-4" /> {presetId ? "Update" : "Save"}
          </Button>
          <Button variant="outline" className="bg-white" onClick={() => { setPresetId(""); setPresetName(""); }}
                  data-testid="audit-preset-new-btn">New</Button>
          <Button variant="outline" className="bg-white" onClick={deletePreset} disabled={!presetId}
                  data-testid="audit-preset-delete-btn">
            <Trash2 className="mr-2 h-4 w-4 text-red-600" /> Delete
          </Button>
        </div>

        <div className="flex flex-wrap items-end gap-2 lg:justify-end" data-testid="audit-retention-panel">
          <div>
            <p className="mb-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">Retention policy</p>
            <Select value={String(retention?.retention_years || 2)} onValueChange={changeRetention}>
              <SelectTrigger className="w-32 bg-white" data-testid="audit-retention-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(retention?.options || [1, 2, 3, 5]).map((y) => (
                  <SelectItem key={y} value={String(y)}>{y} year{y > 1 ? "s" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" className="bg-white" onClick={runArchive} data-testid="audit-archive-now-btn">
            <Archive className="mr-2 h-4 w-4" /> Archive now
          </Button>
          <p className="w-full text-xs text-muted-foreground lg:text-right" data-testid="audit-retention-label">
            {retention
              ? `${retention.active_count} active · ${retention.archived_count} archived · ${retention.eligible_for_archive} eligible. Records are archived, never deleted.`
              : "Loading retention policy…"}
          </p>
        </div>
      </div>

      <div className="grid gap-3 rounded-lg border border-border bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Input value={filters.user_q} onChange={set("user_q")} placeholder="User name or email"
               className="bg-white" data-testid="audit-filter-user" />
        <Input value={filters.lead_id} onChange={set("lead_id")} placeholder="Lead ID"
               className="bg-white" data-testid="audit-filter-lead" />
        <Input value={filters.transaction_id} onChange={set("transaction_id")} placeholder="Transaction ID"
               className="bg-white" data-testid="audit-filter-transaction" />
        <Input value={filters.statement} onChange={set("statement")} placeholder="Statement (id, bank, period)"
               className="bg-white" data-testid="audit-filter-statement" />
        <Select value={filters.action} onValueChange={set("action")}>
          <SelectTrigger className="bg-white" data-testid="audit-filter-action"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {(data?.available_actions || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.field} onValueChange={set("field")}>
          <SelectTrigger className="bg-white" data-testid="audit-filter-field"><SelectValue placeholder="Field changed" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All fields</SelectItem>
            {(data?.available_fields || []).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={filters.date_from} onChange={set("date_from")} className="bg-white"
               data-testid="audit-filter-date-from" />
        <Input type="date" value={filters.date_to} onChange={set("date_to")} className="bg-white"
               data-testid="audit-filter-date-to" />
        <Select value={filters.scope} onValueChange={set("scope")}>
          <SelectTrigger className="bg-white" data-testid="audit-filter-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Active + archived</SelectItem>
            <SelectItem value="active">Active only</SelectItem>
            <SelectItem value="archived">Archived only</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-2 sm:col-span-2 lg:col-span-4">
          <Button onClick={() => load(1)} disabled={busy} data-testid="audit-search-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />} Search
          </Button>
          <Button variant="outline" className="bg-white"
                  onClick={() => { setFilters(EMPTY); load(1, EMPTY); }}
                  data-testid="audit-reset-btn">
            <RotateCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          <span className="self-center text-xs text-muted-foreground" data-testid="audit-total-label">
            {data ? `${data.total} record(s)` : "—"}
          </span>
        </div>
      </div>

      {showActivity && activity && (
        <div className="rounded-lg border border-border bg-white" data-testid="audit-activity-panel">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <h2 className="flex items-center gap-2 font-display text-base font-semibold">
              <Users className="h-4 w-4 text-primary" /> Reviewer activity
              <span className="text-xs font-normal text-muted-foreground">
                {activity.reviewer_count} reviewer(s) · {activity.total_actions} action(s)
                {activity.date_from || activity.date_to
                  ? ` · ${activity.date_from || "start"} → ${activity.date_to || "today"}` : ""}
              </span>
            </h2>
            <Button size="sm" variant="ghost" onClick={() => setShowActivity(false)}
                    data-testid="audit-activity-close-btn">Hide</Button>
          </div>
          <div className="overflow-x-auto p-5">
            <table className="w-full text-sm" data-testid="audit-activity-table">
              <thead>
                <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="py-2 text-left">Reviewer</th>
                  <th className="text-right">Field edits</th>
                  <th className="text-right">Exclusions</th>
                  <th className="text-right">Restorations</th>
                  <th className="text-right">Duplicate decisions</th>
                  <th className="text-right">Row changes</th>
                  <th className="text-right">Document events</th>
                  <th className="text-right">Exports</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {activity.reviewers.map((r, i) => (
                  <tr key={r.user_email} className="border-b border-border/60" data-testid={`activity-row-${i}`}>
                    <td className="py-2">{r.user_name || "—"}
                      <span className="block text-[10px] text-muted-foreground">{r.user_email}</span></td>
                    <td className="num text-right">{r.field_edits}</td>
                    <td className="num text-right">{r.exclusions}</td>
                    <td className="num text-right">{r.restorations}</td>
                    <td className="num text-right">{r.duplicate_decisions}</td>
                    <td className="num text-right">{r.row_changes}</td>
                    <td className="num text-right">{r.document_events}</td>
                    <td className="num text-right">{r.exports}</td>
                    <td className="num text-right font-semibold">{r.total}</td>
                    <td className="text-right text-xs">{when(r.last_activity)}</td>
                  </tr>
                ))}
                {activity.reviewers.length === 0 && (
                  <tr><td colSpan={10} className="py-8 text-center text-sm text-muted-foreground"
                          data-testid="audit-activity-empty">No audit activity in this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border bg-white">
        <table className="w-full text-sm" data-testid="audit-logs-table">
          <thead className="bg-muted">
            <tr className="text-xs uppercase text-muted-foreground">
              <th className="px-3 py-2 text-left">Date / Time</th>
              <th className="px-3 text-left">User</th>
              <th className="px-3 text-left">Lead</th>
              <th className="px-3 text-left">Statement</th>
              <th className="px-3 text-left">Transaction</th>
              <th className="px-3 text-left">Action</th>
              <th className="px-3 text-left">Field</th>
              <th className="px-3 text-left">Old Value</th>
              <th className="px-3 text-left">New Value</th>
              <th className="px-3 text-left">Reason</th>
            </tr>
          </thead>
          <tbody>
            {(data?.entries || []).map((e, i) => (
              <tr key={i} className="border-b border-border/60" data-testid={`audit-log-row-${i}`}>
                <td className="whitespace-nowrap px-3 py-2 text-xs">{when(e.created_at)}</td>
                <td className="px-3 text-xs">{e.user_name}<span className="block text-[10px] text-muted-foreground">{e.user_email}</span></td>
                <td className="px-3 font-mono text-[10px]">{(e.lead_id || "").slice(0, 8)}</td>
                <td className="max-w-[190px] truncate px-3 text-xs">{e.statement_label || e.statement_id}</td>
                <td className="max-w-[170px] truncate px-3 text-xs">
                  {e.file_name || e.description || "—"}
                  <span className="block font-mono text-[10px] text-muted-foreground">
                    {e.document_id ? `doc ${e.document_id.slice(0, 8)}` : e.transaction_id}
                  </span>
                </td>
                <td className="px-3">
                  <Badge variant="outline" className={actionStyle(e.action)}>{e.action_type || e.action}</Badge>
                  {e.source === "archived" && (
                    <Badge variant="outline" className="ml-1 border-amber-200 bg-amber-50 text-[9px] text-amber-700"
                           data-testid="audit-archived-badge">ARCHIVED</Badge>
                  )}
                </td>
                <td className="px-3 text-xs">{e.field || `${e.previous_status} → ${e.new_status}`}</td>
                <td className="max-w-[140px] truncate px-3 text-xs">{e.previous_value ?? e.previous_status ?? "—"}</td>
                <td className="max-w-[140px] truncate px-3 text-xs">{e.new_value ?? e.new_status ?? "—"}</td>
                <td className="max-w-[140px] truncate px-3 text-xs text-muted-foreground">{e.reason || "—"}</td>
              </tr>
            ))}
            {data && data.entries.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-10 text-center text-sm text-muted-foreground"
                      data-testid="audit-logs-empty">No audit records match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground" data-testid="audit-page-label">
          Page {data?.page || 1} of {data?.pages || 1}
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="bg-white" disabled={busy || (data?.page || 1) <= 1}
                  onClick={() => load((data?.page || 1) - 1)} data-testid="audit-prev-page-btn">Previous</Button>
          <Button size="sm" variant="outline" className="bg-white"
                  disabled={busy || (data?.page || 1) >= (data?.pages || 1)}
                  onClick={() => load((data?.page || 1) + 1)} data-testid="audit-next-page-btn">Next</Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Audit records are append-only: they cannot be edited or deleted by any user, including administrators.
      </p>
    </div>
  );
}
